import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowLeft, ArrowLeftRight } from 'lucide-react'
import { Button, Chip, Input, RichTextEditor, Select } from '@/components/ui'
import { EmailThreadPanel, SignaturePicker } from '@/shell/services/email-thread'
import { applySignatureToBody } from '@/lib/utils/signature'
import { sanitizeEmailHtml } from '@/lib/utils/sanitize-html'
import { useHasAnyPermission } from '@/shell/module-system'
import {
  useArchiveRequest,
  useMoveStage,
  usePatchRequest,
  usePreviewTemplate,
  useReassignType,
  useRequestDetail,
  useSendResponse,
  useStages,
  useTemplates,
  useUnarchiveRequest,
} from '../api/queries'
import type { Assignee, Priority, RequestType } from '../api/types'
import { CATEGORY_LABELS, PRIORITY_OPTIONS, PRIORITY_VARIANT } from '../utils/meta'
import { RegulatoryTimeline } from './RegulatoryTimeline'
import { DOC_REQUEST_CREATE_ROLES, useDocRequestForCard } from '../api/doc-requests'
import { RequestDocsDrawer } from './RequestDocsDrawer'
import { VendorDocsPanel } from './VendorDocsPanel'

export interface RegulatoryRequestWorkViewProps {
  requestId: string
}

const BOARD_PATH: Record<RequestType, string> = {
  customer: '/regulatory/customer',
  vendor: '/regulatory/vendor',
}

/**
 * Full-page per-request working view (email-thread-viewer sprint).
 *
 * Shared by both regulatory sibling modules. Header band with a back link
 * that returns to the correct board (derived from `request_type`), then a
 * 2-column grid: LEFT the linked MailDesk conversation, RIGHT the work
 * panel (stage/priority/assignee/due, partner/products/lots, send-response,
 * notes, history) plus the Part F reassign-type action.
 */
export function RegulatoryRequestWorkView({ requestId }: RegulatoryRequestWorkViewProps) {
  const canEdit = useHasAnyPermission(['admin', 'operations', 'purchasing', 'regulatory'])
  const navigate = useNavigate()
  const { data, isLoading } = useRequestDetail(requestId)
  const stages = useStages(data?.request_type)
  const patch = usePatchRequest()
  const move = useMoveStage()
  const archive = useArchiveRequest()
  const unarchive = useUnarchiveRequest()
  const reassign = useReassignType()
  const canRequestDocs = useHasAnyPermission(DOC_REQUEST_CREATE_ROLES)
  const docRequest = useDocRequestForCard(data?.request_type === 'vendor' ? requestId : null)
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false)

  if (isLoading || !data) {
    return (
      <div data-density="comfortable" className="flex h-full flex-col">
        <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
          <Button size="sm" variant="ghost" onClick={() => navigate({ to: '/regulatory/customer' })}>
            <ArrowLeft size={14} />
            Back
          </Button>
          <span className="text-role-section-label">Regulatory request</span>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-8 text-tertiary">
          {isLoading ? 'Loading request…' : 'Request not found.'}
        </div>
      </div>
    )
  }

  const boardPath = BOARD_PATH[data.request_type]
  const stageItems = stages.data?.items ?? []
  const isTerminal =
    stageItems.find((s) => s.id === data.stage_id)?.is_terminal ?? false
  const editable = canEdit && !data.is_archived

  const otherType: RequestType = data.request_type === 'customer' ? 'vendor' : 'customer'

  const handleReassign = () => {
    const label = otherType === 'vendor' ? 'Vendor' : 'Customer'
    if (
      !window.confirm(
        `Move this request to the ${label} board? Its stage will reset to the ${label} board's default.`,
      )
    )
      return
    reassign.mutate(
      { id: data.id, request_type: otherType },
      {
        onSuccess: () => {
          toast.success(`Moved to the ${label} board`)
          navigate({ to: boardPath })
        },
        onError: (e) => toast.error(String(e)),
      },
    )
  }

  return (
    <div data-density="comfortable" className="flex h-full flex-col bg-canvas">
      <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
        <Link to={boardPath}>
          <Button size="sm" variant="ghost">
            <ArrowLeft size={14} />
            Back to board
          </Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-role-data-primary truncate">{data.title}</h1>
        </div>
        <div className="ml-2 flex flex-wrap items-center gap-2">
          <Chip size="xs" variant="neutral">
            {CATEGORY_LABELS[data.category]}
          </Chip>
          <Chip size="xs" variant={PRIORITY_VARIANT[data.priority]}>
            {data.priority}
          </Chip>
          <span className="text-role-metadata text-tertiary">{data.request_type}</span>
          {data.is_overdue ? (
            <Chip size="xs" variant="danger" dot>
              Overdue
            </Chip>
          ) : null}
        </div>
        {canEdit ? (
          <div className="ml-auto flex items-center gap-2">
            {data.is_archived ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => unarchive.mutate(data.id)}
                loading={unarchive.isPending}
              >
                Unarchive
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const reason = window.prompt('Archive reason?') ?? undefined
                  archive.mutate({ id: data.id, reason })
                }}
                loading={archive.isPending}
              >
                Archive
              </Button>
            )}
          </div>
        ) : null}
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_minmax(380px,460px)]">
        {/* LEFT — vendor document request (if any) + linked MailDesk conversation */}
        <div className="min-h-0 overflow-y-auto border-r border-border-subtle">
          {docRequest.data?.request ? <VendorDocsPanel request={docRequest.data.request} /> : null}
          {data.request_type === 'vendor' && !docRequest.data?.request && canRequestDocs && !data.is_archived ? (
            <div className="flex items-center gap-3 border-b border-border-subtle bg-surface px-5 py-3">
              <span className="flex-1 text-sm text-secondary">
                Ask the vendor to upload the documents through a secure link; this card tracks it.
              </span>
              <Button size="sm" variant="secondary" onClick={() => setDocsDrawerOpen(true)}>Create vendor link</Button>
            </div>
          ) : null}
          {data.source_message_index_id || !docRequest.data?.request ? (
            <EmailThreadPanel
              endpoint={`/api/regulatory/requests/${requestId}/email`}
              latestMessageIndexId={data.source_message_index_id}
            />
          ) : null}
        </div>
        <RequestDocsDrawer
          open={docsDrawerOpen}
          onOpenChange={setDocsDrawerOpen}
          regulatoryRequestId={data.id}
          initialVendor={data.partner_id && data.partner_name ? [data.partner_id, data.partner_name] : null}
          initialProducts={(data.product_ids ?? []).map((id, i) => ({
            product_tmpl_id: id, name: data.product_names?.[i] ?? `Product ${id}`,
          }))}
        />

        {/* RIGHT — work panel */}
        <div className="min-h-0 overflow-y-auto bg-surface px-4 py-4">
          <div className="flex flex-col gap-4">
            {/* Editable fields */}
            <section className="grid grid-cols-2 gap-3">
              <Field label="Stage">
                <Select
                  value={data.stage_id ?? ''}
                  disabled={!editable || move.isPending}
                  onChange={(e) => move.mutate({ id: data.id, targetStageId: e.target.value })}
                >
                  {stageItems.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Priority">
                <Select
                  value={data.priority}
                  disabled={!editable}
                  onChange={(e) =>
                    patch.mutate({
                      id: data.id,
                      edits: { priority: e.target.value as Priority },
                    })
                  }
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Assigned to">
                <Select
                  value={data.assigned_to ?? ''}
                  disabled={!editable}
                  onChange={(e) =>
                    patch.mutate({
                      id: data.id,
                      edits: { assigned_to: (e.target.value || null) as Assignee | null },
                    })
                  }
                >
                  <option value="">Unassigned</option>
                  <option value="frank">Frank</option>
                  <option value="shamola">Shamola</option>
                </Select>
              </Field>
              <Field label="Due date">
                <Input
                  type="date"
                  defaultValue={data.due_date ?? ''}
                  disabled={!editable}
                  onBlur={(e) => {
                    const v = e.target.value || null
                    if (v !== data.due_date) {
                      patch.mutate({ id: data.id, edits: { due_date: v } })
                    }
                  }}
                />
              </Field>
            </section>

            {/* Odoo references */}
            <section className="flex flex-col gap-1 border-t border-border-subtle pt-3">
              <Row label="Customer / Vendor" value={data.partner_name} />
              <Row label="Products" value={data.product_names?.join(', ') ?? null} />
              <Row label="Lots" value={data.lot_names?.join(', ') ?? null} />
              {data.source_email_from ? (
                <Row label="From" value={data.source_email_from} />
              ) : null}
              {data.source_email_subject ? (
                <Row label="Subject" value={data.source_email_subject} />
              ) : null}
            </section>

            {/* Part F — move to the sibling board */}
            {editable ? (
              <section className="border-t border-border-subtle pt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={reassign.isPending}
                  onClick={handleReassign}
                >
                  <ArrowLeftRight className="mr-1 h-3.5 w-3.5" />
                  {otherType === 'vendor' ? 'Move to Vendor board' : 'Move to Customer board'}
                </Button>
              </section>
            ) : null}

            {/* Response */}
            {editable ? (
              <SendResponseSection
                requestId={data.id}
                requestType={data.request_type}
                defaultEmail={data.source_email_from ?? ''}
              />
            ) : null}

            {/* Notes */}
            <section className="border-t border-border-subtle pt-3">
              <NotesEditor
                key={data.id}
                requestId={data.id}
                initial={data.notes ?? ''}
                disabled={!editable}
              />
            </section>

            {/* History */}
            <section className="border-t border-border-subtle pt-3">
              <h3 className="text-role-section-label mb-2">Activity</h3>
              <RegulatoryTimeline entries={data.history} />
            </section>

            {isTerminal ? (
              <p className="text-role-metadata text-tertiary">
                This card is in a terminal stage.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-role-metadata text-tertiary">{label}</span>
      {children}
    </label>
  )
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="w-32 shrink-0 text-role-metadata text-tertiary">{label}</span>
      <span className="text-secondary">{value || '—'}</span>
    </div>
  )
}

function NotesEditor({
  requestId,
  initial,
  disabled,
}: {
  requestId: string
  initial: string
  disabled: boolean
}) {
  const [value, setValue] = useState(initial)
  const patch = usePatchRequest()
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-role-section-label">Internal notes</h3>
      <textarea
        className="min-h-[64px] w-full rounded-sm border border-border-default bg-surface p-2 text-sm text-primary"
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
      />
      {!disabled && value !== initial ? (
        <div>
          <Button
            size="sm"
            variant="secondary"
            loading={patch.isPending}
            onClick={() => patch.mutate({ id: requestId, edits: { notes: value } })}
          >
            Save notes
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function SendResponseSection({
  requestId,
  requestType,
  defaultEmail,
}: {
  requestId: string
  requestType: 'customer' | 'vendor'
  defaultEmail: string
}) {
  const templates = useTemplates(requestType)
  const [templateId, setTemplateId] = useState('')
  const [email, setEmail] = useState(defaultEmail)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const previewMut = usePreviewTemplate()
  const send = useSendResponse()

  const applyTemplate = async (tid: string) => {
    setTemplateId(tid)
    if (!tid) return
    try {
      const out = await previewMut.mutateAsync({ templateId: tid, requestId })
      setSubject(out.subject)
      setBody(out.body)
    } catch (e) {
      toast.error(String(e))
    }
  }

  return (
    <section className="flex flex-col gap-2 border-t border-border-subtle pt-3">
      <h3 className="text-role-section-label">Send response</h3>
      <div className="grid grid-cols-2 gap-2">
        <Select value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
          <option value="">Choose template…</option>
          {(templates.data?.items ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <Input
          type="email"
          placeholder="recipient@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
      <RichTextEditor value={body} onChange={setBody} placeholder="Response body" />
      <div className="flex items-center gap-2">
        <span className="text-role-metadata text-tertiary">Signature</span>
        <SignaturePicker
          endpoint={`/api/regulatory/requests/${requestId}/signatures`}
          onApply={(sig) => setBody((b) => applySignatureToBody(b, sig))}
          applyDefaultOnLoad
        />
      </div>
      {body ? (
        <div className="rounded-sm border border-border-subtle bg-surface-sunk p-2">
          <div className="text-role-metadata text-tertiary">Preview</div>
          <div
            className="mt-1 text-sm text-secondary [&_a]:text-accent [&_a]:underline"
            // Sanitized via DOMPurify allowlist before render.
            dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(body) }}
          />
        </div>
      ) : null}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={!email || !subject || !body}
          loading={send.isPending}
          onClick={() => {
            if (!window.confirm(`Send to ${email}?`)) return
            send.mutate(
              {
                id: requestId,
                target_email: email,
                template_id: templateId || undefined,
                subject,
                body,
              },
              {
                onSuccess: () => toast.success(`Response sent to ${email}`),
                onError: (e) => toast.error(String(e)),
              },
            )
          }}
        >
          Send
        </Button>
      </div>
    </section>
  )
}
